import { Button, Icon, Loader } from '@deque/cauldron-react';
import { type TFunction } from 'i18next';
import React, {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProductSlugs } from '@deque/billing-utils';
import {
  ProductNames,
  SupportedIntegrationProductSlugs
} from '~/common/constants';
import { useEnterprises } from '~/common/contexts/enterprises';
import { useGlobalToast } from '~/common/contexts/globalToast';
import {
  type DefaultValueType,
  type IssueTypeField
} from '~/common/utils/integrations-client/jira/getIssueTypeFields';
import Field from './Field/Field';
import styles from './FieldMapping.css';

import {
  IssueFieldSource,
  type JiraFieldMapper,
  JiraFieldMappers,
  JiraFieldMappersKeys
} from '@deque/ui-export-jira';
import useIntegrationLabels from '~/app/hooks/useIntegrationLabels';
import type { FieldMappingField, TemplateFormFields } from '~/app/types';
import { getIntegrationProductInfoFromSlug } from '~/common/utils/get-integration-product-name-from-slug';
import { useFieldsContext } from '../../TemplateWizardForm/IssueTypeFieldsContext';
import { CompatibilityWarning } from './CompatibilityWarning';

export interface SelectOption {
  key: string;
  label: string;
  value: string;
  required?: boolean;
  disabled?: boolean;
  fieldWithMeta?: IssueTypeFieldWithMeta;
}

export interface FieldMappingProps {
  integrationProductSlug:
    | SupportedIntegrationProductSlugs
    | ProductSlugs.axeAuditorIntegration;
  setIsStepDataLoading: Dispatch<SetStateAction<boolean>>;
  connectionId: string;
  token: string;
  setIsStepDataError: Dispatch<SetStateAction<boolean>>;
}

export const getAxeFieldsOptions = (t: TFunction): SelectOption[] => [
  { key: '', label: '', value: '' },
  { label: t('Rule'), key: 'rule', value: 'ruleId' },
  { label: t('Rule Name'), key: 'ruleName', value: 'ruleName' },
  {
    label: t('Experimental Rule Issue'),
    key: 'isExperimental',
    value: 'isExperimental'
  },
  {
    label: t('Needs Review Issue'),
    key: 'needsReview',
    value: 'needsReview'
  },
  { label: t('Description'), key: 'description', value: 'description' },
  { label: t('How to fix'), key: 'help', value: 'help' },
  { label: t('Remediation'), key: 'remediation', value: 'remediation' },
  { label: t('Help URL'), key: 'helpUrl', value: 'helpUrl' },
  { label: t('Issue Summary'), key: 'summary', value: 'summary' },
  { label: t('Selector'), key: 'selector', value: 'selector' },
  { label: t('Ancestry'), key: 'ancestry', value: 'ancestry' },
  {
    label: t('XPath Selector'),
    key: 'xpathSelector',
    value: 'xpathSelector'
  },
  { label: t('Element Source'), key: 'source', value: 'source' },
  {
    label: t('Issue Screenshot URL'),
    key: 'screenshotURL',
    value: 'screenshotURL'
  },
  { label: t('Tags'), key: 'tags', value: 'tags' },
  { label: t('User Tags'), key: 'userTags', value: 'userTags' },
  { label: t('Test name'), key: 'testName', value: 'testName' },
  { label: t('Scan Name'), key: 'scanName', value: 'scanName' },
  {
    label: t('Issue Share URL'),
    key: 'shareURL',
    value: 'shareURL'
  },
  { label: t('Created at'), key: 'createdAt', value: 'createdAt' },
  { label: t('Duplicates'), key: 'duplicates', value: 'duplicates' },
  {
    label: t('Issue Test URL'),
    key: 'testUrl',
    value: 'testUrl'
  },
  {
    label: t('Found by'),
    key: 'foundBy',
    value: 'foundBy'
  },
  {
    label: t('Page Title'),
    key: 'testPageTitle',
    value: 'testPageTitle'
  },
  {
    label: t('Axe version'),
    key: 'axeVersion',
    value: 'axeVersion'
  },
  {
    label: t('Analyzed Values'),
    key: 'analyzedValues',
    value: 'analyzedValues'
  },
  {
    label: t('App Identifier'),
    key: 'appIdentifier',
    value: 'appIdentifier'
  },
  { label: t('Screen Title'), key: 'screenTitle', value: 'screenTitle' },
  {
    label: t('Mobile Platform'),
    key: 'mobilePlatform',
    value: 'mobilePlatform'
  }
];

const getMappingFieldsOptions = (
  fieldsWithMeta: IssueTypeFieldWithMeta[]
): SelectOption[] => {
  const mappedFields = fieldsWithMeta.map<SelectOption>(fieldWithMeta => {
    const { fieldId, name, required } = fieldWithMeta.field;

    return {
      key: fieldId,
      value: fieldId,
      label: name,
      required,
      fieldWithMeta: fieldWithMeta
    };
  });

  return [{ key: '', label: '', value: '' }, ...mappedFields];
};

/** Fields that are handled by server or integration*/
export const ExcludedIntegrationFields = {
  [SupportedIntegrationProductSlugs.jiraDirectCloudIntegration]: [
    'issuetype',
    'project',
    'description',
    'reporter',
    'priority'
  ],
  [SupportedIntegrationProductSlugs.jiraOnPremIntegration]: [
    'issuetype',
    'project',
    'description',
    'reporter',
    'priority'
  ],
  [ProductSlugs.axeAuditorIntegration]: [''],
  [ProductSlugs.customIntegration]: ['']
};

/** Fields that are required by the integration server logic*/
export const RequiredIntegrationFields = {
  [SupportedIntegrationProductSlugs.jiraDirectCloudIntegration]: [
    'description'
  ],
  [SupportedIntegrationProductSlugs.jiraOnPremIntegration]: ['description'],
  [ProductSlugs.axeAuditorIntegration]: [''],
  [ProductSlugs.customIntegration]: ['']
};

export function findAndCalculateSourceType(
  sourceOptions: SelectOption[],
  key: string
): IssueFieldSource {
  const selectedOption = sourceOptions.find(option => option.key === key);

  if (!selectedOption) {
    /* istanbul ignore next */
    return IssueFieldSource.Axe;
  }

  return calculateSourceType(selectedOption.fieldWithMeta);
}

function calculateSourceType(
  fieldWithMeta?: IssueTypeFieldWithMeta
): IssueFieldSource {
  if (!fieldWithMeta) {
    return IssueFieldSource.Axe;
  }

  const idLabelField = fieldWithMeta.mapper?.id === JiraFieldMappersKeys.Labels;

  if (idLabelField) {
    return IssueFieldSource.Combined;
  }

  return fieldWithMeta.isEnum
    ? IssueFieldSource.FixedValue
    : IssueFieldSource.Axe;
}

export function findAndCalculateDefaultValue(
  sourceOptions: SelectOption[],
  key: string
): string {
  const selectedOption = sourceOptions.find(option => option.key === key);
  if (selectedOption) {
    return calculateDefaultValue(selectedOption.fieldWithMeta);
  }

  /* istanbul ignore next */
  return '';
}

function calculateDefaultValue(fieldWithMeta?: IssueTypeFieldWithMeta): string {
  if (fieldWithMeta) {
    if (fieldWithMeta.field.hasDefaultValue) {
      return getDefaultValueAsString(fieldWithMeta?.field.defaultValue);
    }
  }

  return '';
}

export function getDefaultValueAsString(
  defaultValue: DefaultValueType | undefined
): string {
  /* istanbul ignore next */
  if (defaultValue) {
    if (Array.isArray(defaultValue)) {
      if (typeof defaultValue[0] === 'string') {
        return defaultValue[0];
      } else {
        return defaultValue[0].id;
      }
    } else if (typeof defaultValue === 'string') {
      return defaultValue;
    } else if (typeof defaultValue === 'number') {
      return '' + defaultValue;
    } else {
      return defaultValue?.id;
    }
  }

  return '';
}

export function getDefaultValueAsStringOrNumber(
  defaultValue: DefaultValueType | undefined
): string | number {
  /* istanbul ignore next */
  if (defaultValue) {
    if (Array.isArray(defaultValue)) {
      if (typeof defaultValue[0] === 'string') {
        return defaultValue[0];
      } else {
        return defaultValue[0].id;
      }
    } else if (typeof defaultValue === 'string') {
      return defaultValue;
    } else if (typeof defaultValue === 'number') {
      return defaultValue;
    } else {
      return defaultValue?.id;
    }
  }

  return '';
}

export interface IssueTypeFieldWithMeta {
  field: IssueTypeField;
  mapper?: JiraFieldMapper;
  isEnum: boolean;
}

const mappers = Object.values(JiraFieldMappers);

const FieldMapping = ({
  integrationProductSlug,
  setIsStepDataLoading,
  connectionId,
  token,
  setIsStepDataError
}: FieldMappingProps) => {
  const { t } = useTranslation();
  const { setContents } = useGlobalToast();
  const { activeEnterprise } = useEnterprises();
  const { control, getValues, setValue } = useFormContext<TemplateFormFields>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'fieldMapping'
  });

  const projectId = getValues('project');

  const [mappableIntegrationFields, setMappableIntegrationFields] = useState<
    IssueTypeFieldWithMeta[]
  >([]);
  // list of field ids for Jira fields that have been chosen to me mapped
  const [chosenIntegrationFields, setChosenIntegrationFields] = useState<
    string[]
  >([]);

  const { data: issueMappingFields, error, loading } = useFieldsContext();

  const {
    labels,
    error: labelsError,
    loading: labelsLoading
  } = useIntegrationLabels(
    activeEnterprise?.id || '',
    connectionId,
    projectId,
    token
  );

  useEffect(
    () => setIsStepDataLoading(loading || labelsLoading),
    [loading, labelsLoading]
  );

  useEffect(() => {
    setIsStepDataError(!!error || !!labelsError);

    if (error) {
      setContents(
        t('There was an error fetching fields for this issue type.'),
        'error'
      );
    }

    if (labelsError) {
      setContents(
        t('There was an error fetching labels for this project.'),
        'error'
      );
    }
  }, [error, labelsError]);

  useEffect(() => {
    if (!issueMappingFields || !issueMappingFields.length) {
      return;
    }

    const fieldsThatCanBeMapped: IssueTypeFieldWithMeta[] = [];
    const unmappableRequiredFieldNames: string[] = [];

    for (const jiraFieldDefinition of issueMappingFields) {
      const isExcluded = ExcludedIntegrationFields[
        integrationProductSlug
      ].includes(jiraFieldDefinition.fieldId);

      if (!isExcluded) {
        const mapperForThisField = mappers.find(mapper => {
          const field = issueMappingFields.find(
            ({ key }) => key === jiraFieldDefinition.key
          );

          return field && mapper.canMapThisField(field);
        });

        if (mapperForThisField) {
          fieldsThatCanBeMapped.push({
            field: jiraFieldDefinition,
            mapper: mapperForThisField,
            isEnum: mapperForThisField.isEnum
          });
        }

        if (
          !mapperForThisField &&
          jiraFieldDefinition.required &&
          !jiraFieldDefinition.hasDefaultValue
        ) {
          unmappableRequiredFieldNames.push(jiraFieldDefinition.name);
        }
      }
    }

    if (unmappableRequiredFieldNames.length > 0) {
      const fieldNames = unmappableRequiredFieldNames.join(', ');

      setContents(
        t(
          'This issue type has the following required fields that can not be mapped: {{ fieldNames }}. Please set these fields as optional or provide default values.',
          { fieldNames }
        ),
        'error'
      );
      setValue('fieldMapping', []);
      setIsStepDataError(true);

      return;
    }

    const missedRequiredFields = RequiredIntegrationFields[
      integrationProductSlug
    ].filter(
      field =>
        !issueMappingFields.some(
          ({ fieldId, key }) => fieldId === field || key === field
        )
    );

    if (missedRequiredFields.length) {
      const { displayName } = getIntegrationProductInfoFromSlug(
        integrationProductSlug as ProductSlugs
      );
      const fieldNames = missedRequiredFields
        .map(field => `'${field}'`)
        .join(', ');

      setContents(
        t(
          'Missing required field: {{fieldNames}}. Please add this field in your {{integration}} configuration settings.',
          { fieldNames, integration: displayName }
        ),
        'error'
      );

      setIsStepDataError(true);
    }

    setMappableIntegrationFields(fieldsThatCanBeMapped);
  }, [issueMappingFields]);

  useEffect(() => {
    // tells us if we have at lest some mapping in UI
    const hasUIFieldsThatAreMapped = !!fields.length;
    // tell us if we loaded field mapping info that would be needed to calculate default initial set
    const weAlreadyLoadedFieldMapping = mappableIntegrationFields.length !== 0;

    //if we already have some fields mapped or if we have not had a chance to load mapping info yet
    if (hasUIFieldsThatAreMapped || !weAlreadyLoadedFieldMapping) {
      // recalculate
      const usedMappingFieldValues = (
        fields as unknown as FieldMappingField[]
      ).map(field => field.mappingField);

      setChosenIntegrationFields(usedMappingFieldValues);
      return;
    } else {
      // calculate inital set of fields that must be mapped by the user
      const requiredFields = mappableIntegrationFields.reduce(
        (fieldsArray, fieldWithMeta) => {
          if (
            fieldWithMeta.field.required &&
            !fieldWithMeta.field.hasDefaultValue
          ) {
            fieldsArray.push({
              axeField: calculateDefaultValue(fieldWithMeta),
              mappingField: fieldWithMeta.field.fieldId,
              sourceType: calculateSourceType(fieldWithMeta)
            });
          }

          return fieldsArray;
        },
        [] as Array<FieldMappingField>
      );

      // This will update `fields`, which in turn will call this same useEffect again (but then it will go inside if)
      append(requiredFields);
    }
  }, [mappableIntegrationFields, fields]);

  const onSelectIntegrationField = () => {
    const values: Array<FieldMappingField> = getValues('fieldMapping');
    const mappingFields = values.map(({ mappingField }) => mappingField);
    setChosenIntegrationFields(mappingFields);
  };

  const mappingFields: SelectOption[] = useMemo(
    () => getMappingFieldsOptions(mappableIntegrationFields),
    [mappableIntegrationFields]
  );

  const axeFields: SelectOption[] = useMemo(() => getAxeFieldsOptions(t), [t]);

  const isFieldRequired = (field: FieldMappingField) => {
    const targetField = mappableIntegrationFields.find(
      fieldWithMeta => fieldWithMeta.field.fieldId === field.mappingField
    );

    /* istanbul ignore next */
    return targetField?.field?.required && !targetField?.field?.hasDefaultValue;
  };

  const fieldsAreRemaining = fields.length < mappingFields.length - 1;

  const removeField = (index: number) => {
    setValue(
      // react-hook-form's `remove` method removes the field from the form's state, so it's not registered as touched
      // we need to manually set the value to empty string to register it as touched
      `fieldMapping.${index}`,
      { axeField: '', mappingField: '', sourceType: IssueFieldSource.Axe },
      { shouldValidate: true, shouldTouch: true }
    );

    remove(index);
  };

  if (loading || labelsLoading) {
    return <Loader label={t('Loading...')} />;
  }

  const fieldsContent = fields
    .reduce<ReactNode[][]>(
      (result, field, i) => {
        const isRequired = isFieldRequired(field);

        const fieldComponent = (
          <Field
            key={field.id}
            required={isRequired}
            integrationProductSlug={integrationProductSlug}
            axeFields={axeFields}
            mappingFields={mappingFields}
            removeField={removeField}
            index={i}
            onSelectIntegrationField={onSelectIntegrationField}
            chosenIntegrationFields={chosenIntegrationFields}
            integrationProductLabels={labels ?? []}
          />
        );

        if (isRequired) {
          result[0].push(fieldComponent);
        } else {
          result[1].push(fieldComponent);
        }

        return result;
      },
      [[], []]
    )
    .flat();

  return (
    <>
      <CompatibilityWarning />
      <div className={styles.fieldMappingContainer}>
        <div
          className={
            // Add border bottom if there are fields remaining
            fieldsAreRemaining && fields.length !== 0
              ? styles.borderBottom
              : undefined
          }
        >
          {fieldsContent}
        </div>
        {/* Add button to add a new field if there are any fields remaining to map */}
        {fieldsAreRemaining && (
          <Button
            onClick={() =>
              append({
                axeField: '',
                mappingField: '',
                sourceType: IssueFieldSource.Axe
              })
            }
            variant="primary"
            className={styles.addAxeFieldButton}
            thin
          >
            <Icon type="plus" />
            {t(`Add {{ axe }} Field`, {
              axe: ProductNames.axe
            })}
          </Button>
        )}
      </div>
    </>
  );
};

export default FieldMapping;
